import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import usePwaScreen from "../../hooks/usePwaScreen";
import { publicQueue } from "../../api";
import { QRCodeSVG } from "qrcode.react";
import { t as translate, getDir } from "../../i18n";
import BRAND from "../../brand";

const STEPS = { LANG: "lang", ACCESS: "access", FORM: "form", CONFIRM: "confirm", ERROR: "error" };
const INACTIVITY_WARN = 10;
const THEMES = {
  dark_blue: { bg: "#060D1F", card: "rgba(15,23,42,0.95)", accent: "#408DC9", text: "#F1F5F9", sub: "#64748B", border: "rgba(64,141,201,0.25)", input: "rgba(30,41,59,0.9)", btnBg: "linear-gradient(135deg,#408DC9,#3478AD)" },
  dark:      { bg: "#0A0A0A", card: "rgba(20,20,20,0.95)",  accent: "#6366F1", text: "#F8FAFC", sub: "#6B7280", border: "rgba(99,102,241,0.25)", input: "rgba(30,30,30,0.9)", btnBg: "linear-gradient(135deg,#6366F1,#4F46E5)" },
  light:     { bg: "#F0F4F8", card: "rgba(255,255,255,0.98)", accent: "#3478AD", text: "#0F172A", sub: "#475569", border: "rgba(64,141,201,0.2)", input: "#fff", btnBg: "linear-gradient(135deg,#3478AD,#1E40AF)" },
};

// White-label builds (BRAND.dashboard === "modern") take the kiosk colours from the brand:
// primary for buttons/borders, accent as a strip on the card, a soft tint behind it.
// Other builds keep the stock themes untouched.
function brandTheme(base) {
  if (BRAND.dashboard !== "modern") return base;
  const p = BRAND.colorPrimary;
  const isLight = base.text === "#0F172A";
  return {
    ...base,
    accent: p,
    accent2: BRAND.colorAccent,
    border: `${p}40`,
    btnBg: `linear-gradient(135deg, ${p}, #2F6FA3)`,
    ...(isLight ? { bg: "linear-gradient(160deg, #E8F2FA 0%, #F4F8EA 100%)" } : {}),
  };
}

// Accessibility options shown on the assistance selection step.
// label_en/label_tw come from admin-authored copy already in production;
// label_fr/ar/zh were added alongside the i18n framework's STRINGS.fr/ar/zh.
const ACCESS_OPTIONS = [
  { mode: "none",          icon: "✅", label_en: "No Assistance",        label_tw: "Mmoa biara nhia",       label_fr: "Aucune assistance",              label_ar: "بدون مساعدة",         label_zh: "无需协助",     desc_en: "Continue normally",             desc_tw: "Kɔ so",                          desc_fr: "Continuer normalement",              desc_ar: "المتابعة بشكل طبيعي",              desc_zh: "正常继续" },
  { mode: "simple",        icon: "📖", label_en: "Easy Reading",          label_tw: "Kyerɛwee pa",           label_fr: "Lecture facile",                 label_ar: "قراءة سهلة",          label_zh: "简易阅读",     desc_en: "Larger text, simple language",  desc_tw: "Nsɛm kɛseɛ, kasa sɛnea ɛhia",     desc_fr: "Texte plus grand, langage simple",   desc_ar: "نص أكبر، لغة بسيطة",               desc_zh: "更大字体，简单用语" },
  { mode: "hearing",       icon: "👂", label_en: "Hearing Impairment",    label_tw: "Tie yaw",               label_fr: "Déficience auditive",            label_ar: "ضعف السمع",           label_zh: "听力障碍",     desc_en: "Visual alerts only, no audio",  desc_tw: "Aniwa ho ntɛm, audio nni hɔ",     desc_fr: "Alertes visuelles uniquement, pas de son", desc_ar: "تنبيهات مرئية فقط، بدون صوت",  desc_zh: "仅视觉提醒，无声音" },
  { mode: "visual",        icon: "👁",  label_en: "Visual Impairment",     label_tw: "Ani yaw",               label_fr: "Déficience visuelle",            label_ar: "ضعف البصر",           label_zh: "视力障碍",     desc_en: "High contrast & audio alerts",  desc_tw: "Gye ahyɛnsodeɛ audio",            desc_fr: "Contraste élevé et alertes sonores", desc_ar: "تباين عالٍ وتنبيهات صوتية",       desc_zh: "高对比度及语音提醒" },
  { mode: "mobility",      icon: "♿", label_en: "Mobility Assistance",   label_tw: "Nante yaw",             label_fr: "Assistance à la mobilité",       label_ar: "مساعدة الحركة",       label_zh: "行动协助",     desc_en: "Staff will come to you",        desc_tw: "Obrafo bɛba wo nkyɛn",            desc_fr: "Le personnel viendra à vous",        desc_ar: "سيأتي الموظفون إليك",              desc_zh: "工作人员将主动前来" },
  { mode: "communication", icon: "💬", label_en: "Communication Assist",  label_tw: "Kasa mmoa",             label_fr: "Aide à la communication",        label_ar: "مساعدة التواصل",      label_zh: "沟通协助",     desc_en: "Communication card provided",   desc_tw: "Kasa kaadu bɛma wo",              desc_fr: "Carte de communication fournie",     desc_ar: "سيتم توفير بطاقة تواصل",           desc_zh: "将提供沟通卡" },
];

// Generic fallback labels for the platform's standard field names, used
// when a language other than English/Twi is active and the KioskField
// itself has no per-language label column yet (only display_label_en /
// display_label_tw exist on the model today). See docs/PATIENT_LOCALIZATION.md
// "Known gaps" for the full explanation of this limitation.
function accessOptionLabel(opt, lang) {
  return opt[`label_${lang}`] || opt.label_en;
}
function accessOptionDesc(opt, lang) {
  return opt[`desc_${lang}`] || opt.desc_en;
}

export default function KioskPage() {
  const { slug }            = useParams();
  usePwaScreen("kiosk", slug);
  const [config, setConfig] = useState(null);
  const [step,   setStep]   = useState(STEPS.LANG);
  const [lang,   setLang]   = useState("en");
  const [accessMode, setAccessMode] = useState("none");
  const [form,   setForm]   = useState({});
  const [groupOn,    setGroupOn]    = useState(false);
  const [groupNames, setGroupNames] = useState([]);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [configErr, setConfigErr] = useState(null);

  // Inactivity timer
  const [countdown, setCountdown] = useState(null);
  const timerRef   = useRef(null);
  const countRef   = useRef(null);
  const timeoutSecs = config?.timeout_seconds ?? 60;

  // ── Load config ────────────────────────────────────────────────────────────
  useEffect(() => {
    publicQueue.kioskConfig(slug)
      .then(r => setConfig(r.data))
      .catch(() => setConfigErr("This kiosk is not available. Please contact staff."));
  }, [slug]);

  // ── Returning-patient auto-fill: once the phone number looks complete,
  // look up their most recent visit at this facility and fill in their
  // name — so a returning patient doesn't have to retype it. Debounced so
  // it doesn't fire on every keystroke, and only fills a still-empty name
  // field (checked again at write time via the functional update, so it
  // never clobbers something the patient already typed in the meantime).
  // Threshold is intentionally low (6 digits) rather than a full local
  // number's length (10) — phone formats vary, and a stricter gate meant
  // shorter numbers (e.g. ones entered without a leading 0, or numbers
  // from a shorter dialing scheme) never triggered a lookup at all.
  const phoneLookupRef = useRef(null);
  useEffect(() => {
    const phone  = (form.phone || "").trim();
    const digits = phone.replace(/\D/g, "");
    clearTimeout(phoneLookupRef.current);
    if (step !== STEPS.FORM || digits.length < 6 || (form.name || "").trim()) return;
    phoneLookupRef.current = setTimeout(() => {
      publicQueue.lookupPatient(slug, phone)
        .then(res => {
          if (!res.data?.found) return;
          setForm(p => ((p.name || "").trim() ? p : { ...p, name: res.data.patient_name }));
        })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(phoneLookupRef.current);
  }, [form.phone, step, slug]);

  // ── Fullscreen & Kiosk Lock ───────────────────────────────────────────────
  useEffect(() => {
    if (!config) return;

    // Hide cursor
    if (config.kiosk_hide_cursor || config.kiosk_mode) {
      document.body.style.cursor = "none";
    }

    // Auto re-enter fullscreen if exited
    if (config.kiosk_fullscreen !== false || config.kiosk_mode) {
      const onFsChange = () => {
        if (!document.fullscreenElement &&
            (config.kiosk_auto_reenter_fullscreen !== false || config.kiosk_mode)) {
          setTimeout(() => {
            document.documentElement.requestFullscreen().catch(() => {});
          }, 800);
        }
      };
      document.addEventListener("fullscreenchange", onFsChange);
      return () => {
        document.removeEventListener("fullscreenchange", onFsChange);
        document.body.style.cursor = "";
      };
    }
    return () => { document.body.style.cursor = ""; };
  }, [config]);

  // Wake Lock
  useEffect(() => {
    if (!config?.kiosk_prevent_sleep && !config?.kiosk_mode) return;
    let wakeLock = null;
    const acquire = async () => {
      try { wakeLock = await navigator.wakeLock?.request("screen"); } catch {}
    };
    acquire();
    const reacquire = () => {
      if (document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", reacquire);
    return () => {
      document.removeEventListener("visibilitychange", reacquire);
      wakeLock?.release().catch(() => {});
    };
  }, [config]);

  // ── Inactivity reset ───────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    setStep(STEPS.LANG);
    setLang("en");
    setAccessMode("none");
    setForm({});
    setGroupOn(false);
    setGroupNames([]);
    setErrors({});
    setResult(null);
    setCountdown(null);
    clearTimeout(timerRef.current);
    clearInterval(countRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(countRef.current);
    setCountdown(null);
    const activeSteps = [STEPS.FORM, STEPS.LANG, STEPS.ACCESS];
    if (activeSteps.includes(step)) {
      timerRef.current = setTimeout(() => {
        let secs = INACTIVITY_WARN;
        setCountdown(secs);
        countRef.current = setInterval(() => {
          secs--;
          setCountdown(secs);
          if (secs <= 0) { clearInterval(countRef.current); handleReset(); }
        }, 1000);
      }, (timeoutSecs - INACTIVITY_WARN) * 1000);
    }
  }, [step, timeoutSecs, handleReset]);

  useEffect(() => {
    const events = ["mousemove", "keydown", "touchstart", "click"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
      clearInterval(countRef.current);
    };
  }, [resetTimer]);

  // Auto-reset confirm screen after 30s
  useEffect(() => {
    if (step === STEPS.CONFIRM) {
      const t = setTimeout(handleReset, 30000);
      return () => clearTimeout(t);
    }
  }, [step, handleReset]);

  // ── Select language — then decide next step ────────────────────────────────
  const handleSelectLang = (code) => {
    // First interaction — request fullscreen (required user gesture)
    if (config?.kiosk_fullscreen !== false || config?.kiosk_mode) {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }
    setLang(code);
    if (config?.accessibility_enabled !== false) {
      setStep(STEPS.ACCESS);
    } else {
      setStep(STEPS.FORM);
    }
  };

  // ── Submit ticket ──────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const errs = {};
    (config?.fields || []).forEach(f => {
      if (f.is_required && !form[f.field_name]?.toString().trim()) {
        errs[f.field_name] = translate(lang, "kiosk.fieldRequired", { label: fieldLabel(f, lang) });
      }
    });
    // Phone number is a platform-wide requirement, independent of each
    // facility's configurable kiosk fields.
    if (!form.phone?.toString().trim()) {
      errs.phone = translate(lang, "kiosk.phoneRequired");
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const payload = {
        patient_name:       form.name || "",
        patient_title:      form.title || "",
        group_members:      groupOn ? groupNames.map(n => ({ name: n.trim() })).filter(m => m.name) : [],
        patient_phone:      form.phone || "",
        insurance_type:     form.insurance || "",
        reason_for_visit:   form.reason || "",
        language:           lang,
        extra_data:         form,
        accessibility_mode: accessMode,
      };
      const { data } = await publicQueue.issueTicket(slug, payload);
      setResult(data);
      setStep(STEPS.CONFIRM);
    } catch (e) {
      const msg = e.response?.data?.error || translate(lang, "kiosk.ticketFailed");
      setStep(STEPS.ERROR);
      setConfigErr(msg);
    } finally {
      setLoading(false);
    }
  };

  // No language has been picked yet at this point (config load failure
  // happens before the language-select step), so these stay in English.
  if (configErr && step !== STEPS.ERROR) {
    return <FullScreen bg="#060D1F"><ErrorScreen message={configErr} onRetry={handleReset} /></FullScreen>;
  }
  if (!config) {
    return <FullScreen bg="#060D1F"><LoadingScreen /></FullScreen>;
  }

  const theme = brandTheme(THEMES[config.theme] || THEMES.dark_blue);
  const t = (key, vars) => translate(lang, key, vars);

  // Enabled languages from facility config (fallback to en+tw)
  const enabledLanguages = config.enabled_languages?.length
    ? config.enabled_languages
    : [{ code: "en", name: "English", native: "English" }, { code: "tw", name: "Twi", native: "Twi" }];

  // Tracking URL for QR code
  const trackingUrl = result?.tracking_token
    ? `${window.location.origin}/track/${result.tracking_token}`
    : null;

  return (
    <FullScreen bg={theme.bg} dir={step === STEPS.LANG ? "ltr" : getDir(lang)}>
      {/* Inactivity countdown banner */}
      {countdown !== null && (
        <div style={{ ...K.countdownBanner, background: theme.accent }}>
          {t("kiosk.resettingIn")} {countdown}s…
          <button style={K.stayBtn} onClick={resetTimer}>
            {t("kiosk.stillHere")}
          </button>
        </div>
      )}

      <div style={K.shell}>
        {/* Header */}
        <div style={K.header}>
          {config.facility_logo ? (
            <img src={config.facility_logo} alt="logo" style={K.logo} />
          ) : BRAND.dashboard === "modern" && BRAND.logoFull ? (
            <img src={`${process.env.PUBLIC_URL}/${BRAND.logoFull}`} alt={BRAND.name} style={{ ...K.logo, height: "72px", background: "#fff", padding: "8px 16px", borderRadius: "16px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} />
          ) : null}
          <div style={{ ...K.facilityName, color: theme.text }}>{config.facility_name}</div>
        </div>

        {/* ── Step: Language Select ── */}
        {step === STEPS.LANG && (
          <div style={K.card(theme)}>
            <div style={{ ...K.welcome, color: theme.text }}>{config.welcome_en}</div>
            <div style={{ ...K.welcomeSub, color: theme.sub }}>
              {enabledLanguages.length === 1 ? "Touch the button below to get your queue number" : "Please select your language / Yɛsre wo paw wo kasa"}
            </div>
            <div style={{
              ...K.langBtns,
              gridTemplateColumns: `repeat(${Math.min(enabledLanguages.length, 3)}, 1fr)`,
            }}>
              {enabledLanguages.map(l => (
                <LangBtn
                  key={l.code}
                  label={enabledLanguages.length === 1 ? "Get started" : l.native}
                  sublabel={enabledLanguages.length === 1 ? "" : l.code.toUpperCase()}
                  onClick={() => handleSelectLang(l.code)}
                  theme={theme}
                />
              ))}
            </div>
            {config.footer_en && (
              <div style={{ ...K.footer, color: theme.sub }}>{config.footer_en}</div>
            )}
          </div>
        )}

        {/* ── Step: Accessibility Selection ── */}
        {step === STEPS.ACCESS && (
          <div style={K.card(theme)}>
            <div style={{ ...K.welcome, color: theme.text, fontSize: "22px" }}>
              {t("kiosk.assistanceTitle")}
            </div>
            <div style={{ ...K.welcomeSub, color: theme.sub }}>
              {t("kiosk.assistanceSub")}
            </div>
            <div style={K.accessGrid}>
              {ACCESS_OPTIONS.map(opt => (
                <button
                  key={opt.mode}
                  onClick={() => setAccessMode(opt.mode)}
                  style={{
                    ...K.accessBtn(theme),
                    border: `2px solid ${accessMode === opt.mode ? theme.accent : theme.border}`,
                    background: accessMode === opt.mode ? `${theme.accent}22` : theme.input,
                  }}
                >
                  <span style={K.accessIcon}>{opt.icon}</span>
                  <span style={{ ...K.accessLabel, color: accessMode === opt.mode ? theme.accent : theme.text }}>
                    {accessOptionLabel(opt, lang)}
                  </span>
                  <span style={{ ...K.accessDesc, color: theme.sub }}>
                    {accessOptionDesc(opt, lang)}
                  </span>
                </button>
              ))}
            </div>
            <div style={K.formActions}>
              <button style={K.backBtn(theme)} onClick={() => setStep(STEPS.LANG)}>
                {t("kiosk.back")}
              </button>
              <button
                style={K.mainBtn(theme)}
                onClick={() => setStep(STEPS.FORM)}
              >
                {t("kiosk.continue")}
              </button>
            </div>
          </div>
        )}

        {/* ── Step: Form ── */}
        {step === STEPS.FORM && (
          <div style={K.card(theme)}>
            <div style={{ ...K.formTitle, color: theme.text }}>
              {t("kiosk.enterDetails")}
            </div>

            {/* Show selected assistance reminder */}
            {accessMode !== "none" && (() => {
              const opt = ACCESS_OPTIONS.find(o => o.mode === accessMode);
              return opt ? (
                <div style={{ ...K.accessReminder, borderColor: theme.accent, color: theme.accent }}>
                  {opt.icon} {accessOptionLabel(opt, lang)}
                  <button
                    style={{ ...K.changeLink, color: theme.sub }}
                    onClick={() => setStep(STEPS.ACCESS)}
                  >
                    {t("kiosk.change")}
                  </button>
                </div>
              ) : null;
            })()}

            <div style={K.fields}>
              {(config.fields || []).map(field => (
                <React.Fragment key={field.field_name}>
                  <KioskField
                    field={field}
                    lang={lang}
                    value={form[field.field_name] || ""}
                    onChange={v => { setForm(p => ({ ...p, [field.field_name]: v })); setErrors(p => ({ ...p, [field.field_name]: undefined })); }}
                    error={errors[field.field_name]}
                    insuranceRules={field.affects_routing ? config.insurance_rules : []}
                    theme={theme}
                  />
                  {field.field_name === "name" && config.announce_name_enabled && (
                    <div style={{ display: "flex", gap: "12px" }}>
                      {["Mr", "Madam"].map(title => {
                        const on = form.title === title;
                        return (
                          <button
                            key={title}
                            type="button"
                            onClick={() => setForm(p => ({ ...p, title: on ? "" : title }))}
                            style={{
                              flex: 1, padding: "14px", borderRadius: "12px", fontSize: "18px",
                              fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
                              background: on ? theme.accent : theme.input,
                              color: on ? "#fff" : theme.text,
                              border: `1px solid ${on ? theme.accent : theme.border}`,
                            }}
                          >
                            {title}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {config.group_enabled && (config.group_max_size || 8) > 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "18px", color: theme.text, cursor: "pointer" }}>
                  <input
                    type="checkbox" checked={groupOn}
                    onChange={e => { setGroupOn(e.target.checked); if (e.target.checked && groupNames.length === 0) setGroupNames([""]); }}
                    style={{ width: "24px", height: "24px", accentColor: theme.accent2 || undefined }}
                  />
                  I am here with others (family / group)
                </label>
                {groupOn && (
                  <>
                    {groupNames.map((nm, i) => (
                      <div key={i} style={{ display: "flex", gap: "8px" }}>
                        <input
                          value={nm}
                          onChange={e => setGroupNames(p => p.map((x, j) => j === i ? e.target.value : x))}
                          placeholder={`Name of person ${i + 2}`}
                          style={{ flex: 1, background: theme.input, border: `1px solid ${theme.border}`, borderRadius: "12px", padding: "14px 16px", color: theme.text, fontSize: "18px", outline: "none", fontFamily: "inherit" }}
                        />
                        <button type="button" onClick={() => setGroupNames(p => p.filter((_, j) => j !== i))}
                          style={{ background: "transparent", border: `1px solid ${theme.border}`, borderRadius: "12px", color: theme.sub, padding: "0 16px", fontSize: "18px", cursor: "pointer" }}>✕</button>
                      </div>
                    ))}
                    {groupNames.length + 1 < (config.group_max_size || 8) && (
                      <button type="button" onClick={() => setGroupNames(p => [...p, ""])}
                        style={{ background: "transparent", border: `1px dashed ${theme.border}`, borderRadius: "12px", color: theme.accent, padding: "12px", fontSize: "16px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit" }}>
                        + Add another person
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={K.formActions}>
              <button style={K.backBtn(theme)} onClick={() => setStep(config?.accessibility_enabled !== false ? STEPS.ACCESS : STEPS.LANG)}>
                {t("kiosk.back")}
              </button>
              <button
                style={{ ...K.mainBtn(theme), opacity: loading ? 0.6 : 1 }}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? t("kiosk.pleaseWait") : t("kiosk.getNumber")}
              </button>
            </div>
          </div>
        )}

        {/* ── Step: Confirmation ── */}
        {step === STEPS.CONFIRM && result && (
          <div style={K.confirmCard(theme)}>
            <div style={K.confirmTop}>
              <div style={{ ...K.confirmLabel, color: theme.sub }}>
                {t("kiosk.yourQueueNumber")}
              </div>
              <div style={{ ...K.bigNumber, color: theme.accent }}>
                {result.ticket_number}
              </div>

              {result.priority !== "normal" && (
                <div style={K.priorityBadge(result.priority)}>
                  {result.priority === "emergency" ? "🚨" : "⚡"}
                  {t(result.priority === "emergency" ? "kiosk.emergencyPriority" : "kiosk.priorityPatient")}
                </div>
              )}

              {/* Accessibility mode confirmation */}
              {accessMode !== "none" && (() => {
                const opt = ACCESS_OPTIONS.find(o => o.mode === accessMode);
                return opt ? (
                  <div style={K.accessConfirmBadge}>
                    {opt.icon} {accessOptionLabel(opt, lang)}
                  </div>
                ) : null;
              })()}

              <div style={{ ...K.confirmName, color: theme.text }}>{result.patient_name}</div>
              {result.group_tickets?.length > 0 && (
                <div style={{ fontSize: "16px", color: theme.sub, marginTop: "6px" }}>
                  Also: {result.group_tickets.map(g => g.patient_name).join(", ")}
                </div>
              )}

              {result.station_name && (
                <div style={{ ...K.confirmStation, color: theme.sub }}>
                  {t("kiosk.pleaseGoTo")}{" "}
                  <strong style={{ color: theme.text }}>{result.station_name}</strong>
                  {result.zone_name && ` (${result.zone_name})`}
                </div>
              )}
            </div>

            {/* Position + Wait */}
            <div style={K.infoRow}>
              {config.show_position && result.position != null && (
                <InfoBox label={t("kiosk.position")} value={result.position} sub={t("kiosk.inQueue")} theme={theme} />
              )}
              {config.show_wait_time && result.estimated_wait_minutes != null && (
                <InfoBox label={t("kiosk.estWait")} value={`${result.estimated_wait_minutes}`} sub={t("kiosk.minutes")} theme={theme} />
              )}
            </div>

            {/* QR Code — replaces plain text URL */}
            {trackingUrl && (
              <div style={K.qrSection}>
                <div style={{ ...K.qrLabel, color: theme.sub }}>
                  {t("kiosk.scanToTrack")}
                </div>
                <div style={K.qrWrapper(theme)}>
                  <QRCodeSVG
                    value={trackingUrl}
                    size={180}
                    bgColor="transparent"
                    fgColor={theme.text === "#F1F5F9" || theme.text === "#F8FAFC" ? "#ffffff" : "#0F172A"}
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <div style={{ ...K.qrUrl, color: theme.sub }}>
                  {trackingUrl}
                </div>
              </div>
            )}

            <div style={{ ...K.confirmSub, color: theme.sub }}>
              {t("kiosk.waitToBeCalled")}
            </div>

            <button style={{ ...K.mainBtn(theme), marginTop: "8px" }} onClick={handleReset}>
              {t("kiosk.doneIssueAnother")}
            </button>

            <div style={{ ...K.autoReset, color: theme.sub }}>
              {t("kiosk.autoReset")}
            </div>
          </div>
        )}

        {/* ── Step: Error ── */}
        {step === STEPS.ERROR && (
          <ErrorScreen message={configErr} onRetry={handleReset} theme={theme} lang={lang} />
        )}

        {config.show_powered_by !== false && (
          <div style={{ ...K.poweredBy, color: theme.sub }}>Powered by {BRAND.name}</div>
        )}
      </div>
    </FullScreen>
  );
}

// Generic fallback for the platform's standard field names when no
// per-language column exists on KioskField yet (see comment near
// ACCESS_OPTIONS). English/Twi always use the admin-configured label.
function fieldLabel(field, lang) {
  if (lang === "en" || lang === "tw") {
    return field[`display_label_${lang}`] || field.display_label_en;
  }
  const key = `field.${field.field_name}`;
  const fallback = translate(lang, key);
  return fallback === key ? field.display_label_en : fallback;
}

// ── Kiosk Field ───────────────────────────────────────────────────────────────
function KioskField({ field, lang, value, onChange, error, insuranceRules, theme }) {
  const label = fieldLabel(field, lang);
  const base  = {
    background: theme.input, border: `1px solid ${error ? "#EF4444" : theme.border}`,
    borderRadius: "12px", padding: "16px 18px", color: theme.text,
    fontSize: "18px", outline: "none", width: "100%", boxSizing: "border-box",
    fontFamily: "inherit", transition: "border-color 0.15s",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <label style={{ fontSize: "14px", fontWeight: "600", color: theme.sub, letterSpacing: "0.04em" }}>
        {label}
        {field.is_required && <span style={{ color: "#EF4444", marginLeft: "4px" }}>*</span>}
      </label>

      {field.field_name === "insurance" && insuranceRules.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px" }}>
          {insuranceRules.map(rule => (
            <button key={rule.insurance_value} onClick={() => onChange(rule.insurance_value)} style={{
              padding: "14px 12px", borderRadius: "10px", cursor: "pointer",
              border: `2px solid ${value === rule.insurance_value ? theme.accent : theme.border}`,
              background: value === rule.insurance_value ? `${theme.accent}22` : theme.input,
              color: value === rule.insurance_value ? theme.accent : theme.text,
              fontSize: "14px", fontWeight: "600", fontFamily: "inherit", transition: "all 0.15s",
            }}>
              {lang === "tw" ? rule.display_label_tw : rule.display_label_en}
            </button>
          ))}
        </div>
      ) : field.field_type === "select" && field.options_json?.length ? (
        <select style={{ ...base, appearance: "none" }} value={value} onChange={e => onChange(e.target.value)}>
          <option value="">— {translate(lang, "kiosk.select")} —</option>
          {field.options_json.map(opt => (
            <option key={opt.value || opt} value={opt.value || opt}>
              {lang === "tw" ? (opt.label_tw || opt.label || opt) : (opt.label_en || opt.label || opt)}
            </option>
          ))}
        </select>
      ) : field.field_type === "phone" ? (
        <input style={base} type="tel" value={value} onChange={e => onChange(e.target.value)} placeholder="0XX XXX XXXX" inputMode="tel" />
      ) : field.field_type === "number" ? (
        <input style={base} type="number" value={value} onChange={e => onChange(e.target.value)} inputMode="numeric" />
      ) : (
        <input style={base} type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={label} />
      )}

      {error && <span style={{ fontSize: "13px", color: "#EF4444" }}>{error}</span>}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function LangBtn({ label, sublabel, onClick, theme }) {
  return (
    <button onClick={onClick} style={{
      padding: "28px 20px", borderRadius: "16px", cursor: "pointer",
      border: `2px solid ${theme.border}`, background: theme.input,
      color: theme.text, fontFamily: "inherit", transition: "all 0.15s",
      display: "flex", flexDirection: "column", alignItems: "center", gap: "6px",
    }}>
      <span style={{ fontSize: "28px", fontWeight: "800", letterSpacing: "-1px" }}>{label}</span>
      <span style={{ fontSize: "13px", color: theme.sub }}>{sublabel}</span>
    </button>
  );
}

function InfoBox({ label, value, sub, theme }) {
  return (
    <div style={{
      flex: 1, background: `${theme.accent}11`, border: `1px solid ${theme.accent}33`,
      borderRadius: "14px", padding: "16px", textAlign: "center",
    }}>
      <div style={{ fontSize: "12px", color: theme.sub, fontWeight: "600", letterSpacing: "0.06em", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "36px", fontWeight: "800", color: theme.accent, lineHeight: 1, letterSpacing: "-1px" }}>{value}</div>
      <div style={{ fontSize: "12px", color: theme.sub, marginTop: "4px" }}>{sub}</div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
      <div style={{ width: "44px", height: "44px", border: "3px solid rgba(64,141,201,0.2)", borderTopColor: "#408DC9", borderRadius: "50%", animation: "eq-spin 0.7s linear infinite" }} />
      <div style={{ color: "#64748B", fontSize: "16px" }}>Loading kiosk…</div>
    </div>
  );
}

function ErrorScreen({ message, onRetry, theme, lang }) {
  const tt = (key) => (lang ? translate(lang, key) : key === "kiosk.somethingWrong" ? "Something went wrong" : "Try Again");
  return (
    <div style={{ textAlign: "center", padding: "32px" }}>
      <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
      <div style={{ fontSize: "18px", fontWeight: "600", color: theme?.text || "#E2E8F0", marginBottom: "10px" }}>{tt("kiosk.somethingWrong")}</div>
      <div style={{ fontSize: "14px", color: theme?.sub || "#64748B", marginBottom: "24px", lineHeight: "1.6" }}>{message}</div>
      <button onClick={onRetry} style={{
        padding: "14px 28px", borderRadius: "12px", border: "none",
        background: theme?.btnBg || "linear-gradient(135deg,#408DC9,#3478AD)",
        color: "#fff", fontSize: "16px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
      }}>
        {tt("kiosk.tryAgain")}
      </button>
    </div>
  );
}

function FullScreen({ bg, children, dir }) {
  return (
    <div dir={dir || "ltr"} style={{
      minHeight: "100vh", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans','Segoe UI',sans-serif", padding: "20px",
    }}>
      {children}
    </div>
  );
}

// ── Kiosk styles ──────────────────────────────────────────────────────────────
const K = {
  shell: { width: "100%", maxWidth: "560px", display: "flex", flexDirection: "column", gap: "20px", alignItems: "center" },
  header: { display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", textAlign: "center" },
  logo:         { height: "60px", borderRadius: "12px", objectFit: "contain" },
  facilityName: { fontSize: "22px", fontWeight: "700", letterSpacing: "-0.4px" },
  card: (t) => ({
    width: "100%", background: t.card, border: `1px solid ${t.border}`,
    borderRadius: "24px", padding: "36px 32px",
    boxShadow: "0 30px 80px rgba(0,0,0,0.4)", backdropFilter: "blur(20px)",
    display: "flex", flexDirection: "column", gap: "24px",
    ...(t.accent2 ? { borderTop: `6px solid ${t.accent2}` } : {}),
  }),
  welcome:    { fontSize: "26px", fontWeight: "700", letterSpacing: "-0.5px", textAlign: "center" },
  welcomeSub: { fontSize: "15px", textAlign: "center", lineHeight: "1.5" },
  langBtns:   { display: "grid", gap: "14px" },
  footer:     { fontSize: "13px", textAlign: "center", marginTop: "8px" },

  // Accessibility step
  accessGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" },
  accessBtn: (t) => ({
    padding: "14px 12px", borderRadius: "14px", cursor: "pointer",
    background: t.input, fontFamily: "inherit",
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: "6px", textAlign: "center", transition: "all 0.15s",
  }),
  accessIcon:  { fontSize: "28px", lineHeight: 1 },
  accessLabel: { fontSize: "13px", fontWeight: "700", lineHeight: 1.2 },
  accessDesc:  { fontSize: "11px", lineHeight: 1.3 },
  accessReminder: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 14px", borderRadius: "10px", fontSize: "13px",
    fontWeight: "600", border: "1px solid",
  },
  changeLink: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: "12px", fontFamily: "inherit", textDecoration: "underline",
  },
  accessConfirmBadge: {
    padding: "6px 14px", borderRadius: "8px", fontSize: "13px", fontWeight: "700",
    background: "rgba(34,197,94,0.12)", color: "#22C55E",
    border: "1px solid rgba(34,197,94,0.3)",
  },

  formTitle:   { fontSize: "22px", fontWeight: "700", letterSpacing: "-0.3px", marginBottom: "4px" },
  fields:      { display: "flex", flexDirection: "column", gap: "18px" },
  formActions: { display: "flex", gap: "12px", marginTop: "8px" },
  backBtn: (t) => ({
    padding: "14px 20px", borderRadius: "12px", border: `1px solid ${t.border}`,
    background: "transparent", color: t.sub, fontSize: "15px",
    fontWeight: "600", cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
  }),
  mainBtn: (t) => ({
    flex: 1, padding: "16px 24px", borderRadius: "12px", border: "none",
    background: t.btnBg, color: "#fff", fontSize: "17px",
    fontWeight: "700", cursor: "pointer", fontFamily: "inherit",
    transition: "opacity 0.15s", letterSpacing: "-0.2px",
  }),
  confirmCard: (t) => ({
    width: "100%", background: t.card, border: `1px solid ${t.border}`,
    borderRadius: "24px", padding: "36px 32px",
    boxShadow: "0 30px 80px rgba(0,0,0,0.5)", backdropFilter: "blur(20px)",
    display: "flex", flexDirection: "column", gap: "20px", textAlign: "center",
  }),
  confirmTop:    { display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" },
  confirmLabel:  { fontSize: "12px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase" },
  bigNumber:     { fontSize: "80px", fontWeight: "900", letterSpacing: "-3px", lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  confirmName:   { fontSize: "22px", fontWeight: "700" },
  confirmStation:{ fontSize: "15px" },
  infoRow:       { display: "flex", gap: "12px" },
  confirmSub:    { fontSize: "14px", lineHeight: "1.6" },
  autoReset:     { fontSize: "12px", marginTop: "4px" },
  priorityBadge: (priority) => ({
    display: "inline-flex", alignItems: "center", gap: "6px",
    padding: "6px 14px", borderRadius: "8px", fontSize: "13px", fontWeight: "700",
    background: priority === "emergency" ? "rgba(239,68,68,0.15)" : "rgba(234,179,8,0.15)",
    color:      priority === "emergency" ? "#EF4444" : "#EAB308",
    border: `1px solid ${priority === "emergency" ? "#EF444433" : "#EAB30833"}`,
  }),

  // QR code section
  qrSection: { display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" },
  qrLabel:   { fontSize: "12px", fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase" },
  qrWrapper: (t) => ({
    padding: "16px", borderRadius: "16px",
    background: t.text === "#F1F5F9" || t.text === "#F8FAFC" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    border: `1px solid ${t.border}`,
  }),
  qrUrl: { fontSize: "11px", fontFamily: "monospace", wordBreak: "break-all", maxWidth: "280px", lineHeight: 1.5 },

  countdownBanner: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
    padding: "14px 24px", display: "flex", alignItems: "center",
    justifyContent: "center", gap: "16px", color: "#fff",
    fontSize: "16px", fontWeight: "600",
  },
  stayBtn: {
    background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
    borderRadius: "8px", color: "#fff", padding: "6px 14px",
    cursor: "pointer", fontWeight: "600", fontFamily: "inherit", fontSize: "14px",
  },
  poweredBy: { fontSize: "11px", letterSpacing: "0.04em", marginTop: "8px" },
};
